import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import gsap from 'gsap';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { WELCOME_GREETING_STORAGE_KEY } from '../lib/welcomeGreeting';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { cn } from '../lib/utils';

function displayName(user) {
  if (!user) return '';
  if (user.full_name && String(user.full_name).trim()) return String(user.full_name).trim();
  const email = user.email || '';
  const local = email.split('@')[0] || '';
  if (!local) return 'there';
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const PARTICLE_STYLE = [
  { left: '8%', top: '18%', width: 3, height: 3, delay: 0 },
  { left: '88%', top: '22%', width: 4, height: 4, delay: 0.08 },
  { left: '14%', top: '72%', width: 3, height: 3, delay: 0.16 },
  { left: '78%', top: '68%', width: 5, height: 5, delay: 0.24 },
  { left: '48%', top: '8%', width: 2, height: 2, delay: 0.32 },
  { left: '52%', top: '88%', width: 3, height: 3, delay: 0.4 },
];

export function WelcomeGreeting({ user }) {
  const [open, setOpen] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const shimmerRef = useRef(null);
  const iconWrapRef = useRef(null);
  const ctxRef = useRef(null);

  const handleOpenChange = useCallback((next) => {
    setOpen(next);
    if (!next) {
      try {
        sessionStorage.setItem(WELCOME_GREETING_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem(WELCOME_GREETING_STORAGE_KEY)) return;
    } catch {
      return;
    }
    setOpen(true);
  }, [user]);

  useLayoutEffect(() => {
    if (!open || prefersReducedMotion) return;

    let cancelled = false;
    let rafAttempts = 0;
    const maxRafAttempts = 45;

    const run = () => {
      if (cancelled) return;
      const root = rootRef.current;
      const panel = panelRef.current;
      const iconEl = iconWrapRef.current;
      if (!root || !panel || !iconEl) {
        rafAttempts += 1;
        if (rafAttempts < maxRafAttempts) requestAnimationFrame(run);
        return;
      }

      ctxRef.current?.revert();
      ctxRef.current = null;

      const ctx = gsap.context(() => {
        const q = gsap.utils.selector(root);
        const lines = q('[data-welcome-line]');
        const particles = q('[data-welcome-particle]');

        gsap.set(panel, {
          opacity: 0,
          scale: 0.78,
          y: 64,
          rotateX: 16,
          filter: 'blur(22px)',
          transformPerspective: 1200,
          transformOrigin: '50% 12%',
        });
        gsap.set(iconEl, { scale: 0, rotation: -38 });
        gsap.set(lines, { opacity: 0, y: 40 });
        gsap.set(particles, { scale: 0, opacity: 0 });
        if (shimmerRef.current) {
          gsap.set(shimmerRef.current, { xPercent: -120, skewX: -18, opacity: 0.45 });
        }

        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        tl.to(panel, {
          opacity: 1,
          scale: 1,
          y: 0,
          rotateX: 0,
          filter: 'blur(0px)',
          duration: 1.08,
          ease: 'power4.out',
        })
          .to(
            iconEl,
            {
              scale: 1,
              rotation: 0,
              duration: 0.92,
              ease: 'elastic.out(1, 0.5)',
            },
            '-=0.78'
          )
          .to(
            lines,
            {
              opacity: 1,
              y: 0,
              duration: 0.58,
              stagger: 0.1,
              ease: 'power2.out',
            },
            '-=0.62'
          )
          .to(
            particles,
            {
              scale: 1,
              opacity: 1,
              duration: 0.45,
              stagger: 0.055,
              ease: 'back.out(2.2)',
            },
            '-=0.55'
          );

        if (shimmerRef.current) {
          tl.to(
            shimmerRef.current,
            {
              xPercent: 125,
              opacity: 0,
              duration: 1.15,
              ease: 'power2.inOut',
            },
            '-=0.95'
          );
        }

        gsap.to(iconEl, {
          y: '+=5',
          repeat: -1,
          yoyo: true,
          duration: 2.6,
          ease: 'sine.inOut',
        });
      }, root);

      ctxRef.current = ctx;
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ctxRef.current?.revert();
      ctxRef.current = null;
    };
  }, [open, prefersReducedMotion]);

  const name = useMemo(() => displayName(user), [user]);

  if (!user) return null;

  const shellMotion = !prefersReducedMotion;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayClassName={cn(
          shellMotion && 'bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(59,130,246,0.35),transparent_55%)] bg-zinc-950/88 backdrop-blur-xl',
          !shellMotion && 'bg-black/75 backdrop-blur-md'
        )}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 w-full max-w-[min(100vw-1.5rem,28rem)] translate-x-[-50%] translate-y-[-50%] gap-0 border-0 bg-transparent p-0 shadow-none duration-0 sm:rounded-none',
          'data-[state=closed]:duration-200'
        )}
      >
        <div ref={rootRef} className={cn('w-full perspective-[1400px]', shellMotion && 'pt-1')}>
          <div
            ref={panelRef}
            className={cn(
              'relative mx-auto w-full max-w-md transform-gpu will-change-[transform,opacity,filter]',
              shellMotion && 'motion-safe:[transform-style:preserve-3d]'
            )}
          >
            <div
              className={cn(
                'rounded-2xl p-[1px]',
                shellMotion
                  ? 'bg-gradient-to-br from-sky-400/50 via-violet-500/45 to-fuchsia-500/50 bg-[length:240%_240%] motion-safe:animate-welcome-border-flow'
                  : 'border border-border bg-border'
              )}
            >
              <div className="relative overflow-hidden rounded-[15px] bg-zinc-950/[0.97] px-8 pb-8 pt-11 shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_32px_96px_-24px_rgba(0,0,0,0.85),0_0_80px_-20px_rgba(59,130,246,0.35)] backdrop-blur-2xl">
                {shellMotion && (
                  <>
                    <div
                      className="pointer-events-none absolute -left-1/4 top-0 h-[min(280px,55%)] w-[70%] rounded-full bg-sky-500/25 blur-[64px] motion-safe:animate-welcome-aurora"
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute -right-1/4 bottom-0 h-[min(240px,50%)] w-[60%] rounded-full bg-fuchsia-600/20 blur-[56px] motion-safe:animate-welcome-aurora motion-safe:[animation-delay:-3.5s]"
                      aria-hidden
                    />
                  </>
                )}

                {shellMotion &&
                  PARTICLE_STYLE.map((p, i) => (
                    <div
                      key={i}
                      data-welcome-particle
                      className="pointer-events-none absolute rounded-full bg-sky-300/90 shadow-[0_0_12px_rgba(56,189,248,0.85)]"
                      style={{
                        left: p.left,
                        top: p.top,
                        width: p.width,
                        height: p.height,
                      }}
                      aria-hidden
                    />
                  ))}

                {shellMotion && (
                  <div
                    ref={shimmerRef}
                    className="pointer-events-none absolute inset-0 z-[15] overflow-hidden rounded-[15px]"
                    aria-hidden
                  >
                    <div className="absolute -left-1/2 top-0 h-full w-[85%] -skew-x-[18deg] bg-gradient-to-r from-transparent via-white/[0.18] to-transparent" />
                  </div>
                )}

                <div className="relative z-20 flex flex-col items-center gap-3 text-center sm:text-center">
                  <div className="flex w-full justify-center">
                    <div
                      ref={iconWrapRef}
                      className={cn(
                        'relative flex h-[3.75rem] w-[3.75rem] items-center justify-center rounded-full',
                        'bg-gradient-to-br from-sky-500/30 via-primary/25 to-violet-600/35 ring-1 ring-white/20',
                        'shadow-[0_0_48px_-6px_rgba(59,130,246,0.65),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
                      )}
                    >
                      <Sparkles
                        className={cn(
                          'relative h-8 w-8 text-sky-100 drop-shadow-[0_0_20px_rgba(56,189,248,0.9)]',
                          shellMotion && 'motion-safe:animate-welcome-sparkle'
                        )}
                        aria-hidden
                      />
                    </div>
                  </div>

                  <DialogTitle
                    className="text-center font-heading text-xl tracking-tight text-zinc-50 sm:text-center md:text-2xl"
                    data-welcome-line
                  >
                    Welcome{name && name !== 'there' ? `, ${name}` : ''}
                  </DialogTitle>
                  <DialogDescription
                    className="text-center text-base leading-relaxed text-zinc-300 sm:text-center"
                    data-welcome-line
                  >
                    Thanks for choosing AERTA. Your dashboard is ready—risk views, reporting, and pilot workflows are here
                    when you are.
                  </DialogDescription>
                  <p className="text-center text-xs text-zinc-500" data-welcome-line>
                    From the team at Aethereal
                  </p>
                  <div className="flex w-full justify-center pt-2" data-welcome-line>
                    <Button
                      type="button"
                      className={cn(
                        'min-w-[148px] bg-gradient-to-b from-primary to-blue-600 font-medium shadow-[0_12px_40px_-12px_rgba(59,130,246,0.65)] transition-[transform,box-shadow] duration-300',
                        shellMotion && 'motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-[0_16px_48px_-8px_rgba(59,130,246,0.75)] motion-safe:active:translate-y-0'
                      )}
                      onClick={() => handleOpenChange(false)}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
